import {
  Box,
  Button,
  Collapse,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  IconButton,
  Image,
  Text,
  useDisclosure,
} from "@chakra-ui/react";

import { HiChevronDown, HiOutlineMenu } from "react-icons/hi";
import { HamburgerMenuProps, NavLinkItem } from "../../@types";

function ItemIcon({ icon, label }: Pick<NavLinkItem, "icon" | "label">) {
  if (!icon) return null;
  if (typeof icon === "string") {
    return icon.trim().startsWith("<svg") ? (
      <Box
        dangerouslySetInnerHTML={{ __html: icon }}
        sx={{
          "& svg": {
            width: "20px",
            height: "20px",
            stroke: "currentColor",
            fill: "currentColor",
          },
        }}
      />
    ) : (
      <Image src={icon} alt={label} boxSize="20px" />
    );
  }
  if (typeof icon === "function") return <Box as={icon} />;
  return <>{icon}</>;
}

type HamburgerNavItemProps = {
  item: NavLinkItem;
  currentPath: string;
  isLoggedIn: boolean;
  routeChangeFn: (href: string) => void;
  onNavigate: () => void;
  navButtonProps: (item: NavLinkItem) => Record<string, unknown>;
  buttonTextWithIconProps?: HamburgerMenuProps["buttonTextWithIconProps"];
  buttonTextWithoutIconProps?: HamburgerMenuProps["buttonTextWithoutIconProps"];
  activeButtonTextProps?: HamburgerMenuProps["activeButtonTextProps"];
  subMenuListItemProps?: HamburgerMenuProps["hamburgerSubMenuListItemProps"];
};

function HamburgerNavItem({
  item,
  currentPath,
  isLoggedIn,
  routeChangeFn,
  onNavigate,
  navButtonProps,
  buttonTextWithIconProps,
  buttonTextWithoutIconProps,
  activeButtonTextProps,
  subMenuListItemProps,
}: HamburgerNavItemProps) {
  const subLinks = (item.subLinks || []).filter(
    (sub) => !(sub.authRequired && !isLoggedIn),
  );
  const hasSubLinks = subLinks.length > 0;
  const isActive = item.label === currentPath;
  const { isOpen, onToggle } = useDisclosure();

  const label = (
    <Text
      flex={1}
      textAlign="left"
      {...(item.icon ? buttonTextWithIconProps : buttonTextWithoutIconProps)}
      {...(isActive && activeButtonTextProps)}
    >
      {item.label}
    </Text>
  );

  if (hasSubLinks) {
    return (
      <Box>
        <Button
          w="full"
          gap={2}
          onClick={onToggle}
          rightIcon={
            <Box
              as={HiChevronDown}
              transition="transform 0.2s ease"
              transform={isOpen ? "rotate(180deg)" : "rotate(0deg)"}
            />
          }
          {...navButtonProps(item)}
        >
          {item.icon && (
            <Box as="span" fontSize="xl">
              <ItemIcon icon={item.icon} label={item.label} />
            </Box>
          )}
          {label}
        </Button>
        <Collapse in={isOpen} animateOpacity>
          <Flex direction="column" pl={6} py={1} gap={1}>
            {subLinks.map((sub, subIndex) => (
              <Button
                key={`ham__sub__${subIndex}`}
                w="full"
                justifyContent="flex-start"
                size="sm"
                colorScheme={
                  sub.label === currentPath ? "primary" : "transparent"
                }
                color={sub.label === currentPath ? "primary.500" : "grey.600"}
                gap={2}
                onClick={() => {
                  if (sub.href) routeChangeFn(sub.href);
                  onNavigate();
                }}
                {...subMenuListItemProps}
              >
                {sub.icon && (
                  <Box as="span" fontSize="lg">
                    <ItemIcon icon={sub.icon} label={sub.label} />
                  </Box>
                )}
                {sub.label}
              </Button>
            ))}
          </Flex>
        </Collapse>
      </Box>
    );
  }

  return (
    <Button
      w="full"
      gap={2}
      onClick={() => {
        if (item.href) routeChangeFn(item.href);
        onNavigate();
      }}
      {...navButtonProps(item)}
    >
      {item.icon && (
        <Box as="span" fontSize="xl">
          <ItemIcon icon={item.icon} label={item.label} />
        </Box>
      )}
      {label}
    </Button>
  );
}

function HamburgerMenu({
  navLinkItems,
  size = "md",
  currentPath,
  logo,
  brandName,
  hamburgerIconProps,
  drawerProps,
  drawerOverlayProps,
  drawerContentProps,
  drawerCloseButtonProps,
  drawerBodyProps,
  hamburgerMenuListProps,
  hamburgerMenuListItemProps,
  hamburgerActiveMenuListItemProps,
  hamburgerSubMenuListItemProps,
  onLoginClick,
  onRegisterClick,
  onLogout,
  routeChangeFn,
  isLoggedIn = false,
  loginButtonProps,
  registerButtonProps,
  logoutButtonProps,
  buttonTextWithIconProps,
  buttonTextWithoutIconProps,
  activeButtonTextProps,
  hamburgerLogoProps,
}: HamburgerMenuProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const navButtonProps = (item: NavLinkItem) => {
    const isActive = item.label === currentPath;
    return {
      justifyContent: "flex-start",
      colorScheme: "transparent",
      color: "grey.700",
      ...(isActive && {
        colorScheme: "primary",
        color: "primary.500",
        bg: "primary.50",
        borderLeft: "3px solid",
        borderColor: "primary.500",
        ...hamburgerActiveMenuListItemProps,
      }),
      ...hamburgerMenuListItemProps,
    };
  };

  const visibleItems = navLinkItems?.filter(
    (item) => !(item.authRequired && !isLoggedIn),
  );

  return (
    <>
      <IconButton
        variant="unstyled"
        fontSize={"1.75rem"}
        onClick={onOpen}
        sx={{
          transition: "all 0.2s ease-in-out",
        }}
        _hover={{
          color: "primary.500",
        }}
        {...hamburgerIconProps}
        aria-label="hamburger_menu"
        icon={<HiOutlineMenu />}
      />

      <Drawer
        placement="right"
        {...drawerProps}
        onClose={onClose}
        isOpen={isOpen}
        size={size}
      >
        <DrawerOverlay {...drawerOverlayProps} />
        <DrawerContent
          sx={{
            paddingTop: "env(safe-area-inset-top)",
            paddingBottom: "env(safe-area-inset-bottom)",
          }}
          {...drawerContentProps}
        >
          <DrawerCloseButton size="lg" {...drawerCloseButtonProps} />
          {(logo || brandName) && (
            <DrawerHeader borderBottomWidth="1px" py={3}>
              <Flex align="center" gap={3}>
                {logo && (
                  <Image
                    src={logo}
                    alt={brandName || "Logo"}
                    maxH="2.25rem"
                    {...hamburgerLogoProps}
                  />
                )}
                {brandName && (
                  <Text fontSize="lg" fontWeight={700} noOfLines={1}>
                    {brandName}
                  </Text>
                )}
              </Flex>
            </DrawerHeader>
          )}
          <DrawerBody
            mt={logo || brandName ? 2 : "2.5rem"}
            {...drawerBodyProps}
          >
            <Flex direction="column" gap={1} {...hamburgerMenuListProps}>
              {visibleItems?.map((item, index) => (
                <HamburgerNavItem
                  key={`nav__ham__link__${index}`}
                  item={item}
                  currentPath={currentPath}
                  isLoggedIn={isLoggedIn}
                  routeChangeFn={routeChangeFn}
                  onNavigate={onClose}
                  navButtonProps={navButtonProps}
                  buttonTextWithIconProps={buttonTextWithIconProps}
                  buttonTextWithoutIconProps={buttonTextWithoutIconProps}
                  activeButtonTextProps={activeButtonTextProps}
                  subMenuListItemProps={hamburgerSubMenuListItemProps}
                />
              ))}
            </Flex>

            <Divider my={4} />

            {!isLoggedIn ? (
              <Flex direction="column" gap={2}>
                <Button
                  w="full"
                  colorScheme="primary"
                  onClick={() => {
                    onLoginClick();
                    onClose();
                  }}
                  {...loginButtonProps}
                >
                  Login
                </Button>
                <Button
                  w="full"
                  variant="outline"
                  colorScheme="primary"
                  onClick={() => {
                    onRegisterClick();
                    onClose();
                  }}
                  {...registerButtonProps}
                >
                  Register
                </Button>
              </Flex>
            ) : (
              <Button
                w="full"
                variant="outline"
                colorScheme="red"
                onClick={() => {
                  onLogout();
                  onClose();
                }}
                {...logoutButtonProps}
              >
                Logout
              </Button>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}

export default HamburgerMenu;
